import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { DefaultButton } from '@bingads-webui-react/fabric-control';
import {
  UAPPlacementTypeAlias as EPT,
  useImageExperienceAdPreviewModel,
  AdPreviewCarouselLayoutEx,
  UAPAdExtensionType as EAdExtensionType,
  AdPreviewAdExtensionComponents,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { PerfMaxAdPreviewControls } from './ad-preview-controls';
import previewPlaceholder from '../media/preview-placeholder.png';
import { getAdPreviewStyles } from './style';
import {
  pmaxRequiredAspectRatios,
  audienceMMAV2RequiredAspectRatios,
  adPreviewConfig as PMaxAdPreviewConfig,
  adPreviewConfigV2 as PMaxAdPreviewConfigV2,
  adPreviewConfigV3 as PMaxAdPreviewConfigV3,
} from './shared';

export const shortHeadlineMaxLength = 40;
export const longHeadlineMaxLength = 90;
export const descriptionMaxLength = 90;

const getPMaxExtraPlacementInfo = hasLogo => ({
  [EPT.PMax.AudienceWideImage]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('MSN feed'),
  },
  [EPT.PMax.MSEdgeSingleCarousel]: {
    aspectRatioUIString: '1.78:1',
    previewTitle: _TL_('Microsoft Edge single carousel'),
  },
  [EPT.PMax.MSNInArticle]: {
    aspectRatioUIString: '1.5:1',
    previewTitle: _TL_('MSN in-article ad'),
  },
  [EPT.PMax.AudienceSquareImage]: {
    aspectRatioUIString: '1:1',
    previewTitle: _TL_('MSN square image'),
  },
  [EPT.PMax.MSNSmallHeadline]: {
    aspectRatioUIString: '1.33:1',
    previewTitle: _TL_('MSN small headline'),
  },
  [EPT.PMax.MMAWideImage]: {
    aspectRatioUIString: '4:1',
    previewTitle: _TL_('Wide image'),
  },
  [EPT.PMax.MMAVerticalImage]: {
    aspectRatioUIString: '1:2',
    previewTitle: _TL_('Vertical image'),
  },
  [EPT.PMax.MMALandscapeImage]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('Landscape Image Multimedia Ad'),
    hasLogo,
  },
  [EPT.BingSearch.Text]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Bing search ad'),
    hasLogo,
  },
  [EPT.Outlook.Mobile]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (mobile)'),
    hasLogo,
  },
  [EPT.Outlook.Web]: {
    aspectRatioUIString: '',
    previewTitle: _TL_('Microsoft Outlook web (computer)'),
    hasLogo,
  },
  [EPT.PMax.MMAV2MSEdgeCarousel]: {
    aspectRatioUIString: '2.01:1',
    previewTitle: _TL_('Microsoft Edge carousel'),
  },
  [EPT.PMax.MMAV2MSNSingleCarousel]: {
    aspectRatioUIString: '1.69:1',
    previewTitle: _TL_('MSN Single Carousel'),
  },
  [EPT.PMax.MMAV2MSNRestoday]: {
    aspectRatioUIString: '1.24:1',
    previewTitle: _TL_('MSN Restoday'),
  },
  [EPT.PMax.MMAV2MSNPartnerStripe]: {
    aspectRatioUIString: '1.53:1',
    previewTitle: _TL_('MSN Partner Stripe'),
  },
  [EPT.PMax.MMAV2MSNRiver]: {
    aspectRatioUIString: '1.91:1',
    previewTitle: _TL_('MSN feed'),
    hasLogo,
  },
  [EPT.PMax.MSEdgeMobile]: {
    aspectRatioUIString: '1:1',
    previewTitle: _TL_('Microsoft Edge mobile'),
    hasLogo,
  },
});

const AdExtensionTypes = [
  EAdExtensionType.SiteLink,
  EAdExtensionType.Callout,
  EAdExtensionType.Promotion,
  EAdExtensionType.StructuredSnippet,
  EAdExtensionType.Price,
];

export const getExtensionData = (extensions, extensionType, extensionFormatters) => {
  switch (extensionType) {
    case EAdExtensionType.SiteLink: {
      const siteLinks = _.result(extensions, EAdExtensionType.SiteLink, []).slice(0, 4); // only show 4 at most.
      if (_.isFunction(extensionFormatters[EAdExtensionType.SiteLink])) {
        return siteLinks.map(siteLink => extensionFormatters[EAdExtensionType.SiteLink](siteLink));
      }
      return siteLinks;
    }
    case EAdExtensionType.Callout: {
      const callOuts = _.result(extensions, EAdExtensionType.Callout, []).slice(0, 4); // only show 4 at most.
      if (_.isFunction(extensionFormatters[EAdExtensionType.Callout])) {
        return callOuts.map(callOut => extensionFormatters[EAdExtensionType.Callout](callOut));
      }
      return callOuts;
    }
    case EAdExtensionType.Promotion: {
      const promotions = _.result(extensions, EAdExtensionType.Promotion, []).slice(0, 1); // only show 1 at most.
      if (_.isFunction(extensionFormatters[EAdExtensionType.Promotion])) {
        return promotions.map(promotion => extensionFormatters[EAdExtensionType.Promotion](promotion));
      }
      return promotions;
    }
    case EAdExtensionType.StructuredSnippet: {
      const structuredSnippets = _.result(extensions, EAdExtensionType.StructuredSnippet, []).slice(0, 1).map(({ Category, Texts }) => ({
        Category,
        Texts: Texts.slice(0, 3),
      })); // only show 3 values at most.
      if (_.isFunction(extensionFormatters[EAdExtensionType.StructuredSnippet])) {
        return structuredSnippets.map(structuredSnippet => extensionFormatters[EAdExtensionType.StructuredSnippet](structuredSnippet));
      }
      return structuredSnippets;
    }
    case EAdExtensionType.Price: {
      const prices = _.result(extensions, EAdExtensionType.Price, []).reduce((acc, { TableRows }) => [...acc, ...TableRows], []).slice(0, 8); // only show 8 at most.
      if (_.isFunction(extensionFormatters[EAdExtensionType.Price])) {
        return prices.map(price => extensionFormatters[EAdExtensionType.Price](price));
      }
      return prices;
    }
    default:
      return [];
  }
};

export const PerfMaxAdPreview = (props) => {
  const {
    extraPlacementInfo: extraPlacementInfoProp,
    imageList,
    updateImageList,
    previewImagesSharedState,
    setPreviewImagesSharedState,
    isMSANAd,
    enableAutoPlay,
    i18n,
    logoPreviewEnabled,
    customPlacementContainerClassName,
    customPlacementHeaderClassName,
    isAdExtensionPreviewEnabled,
    extensionsAssociations,
    extensionFormatters,
    isMMAV2Enabled,
    isImageFittingEnabled,
    activity,
    isPMaxAspectRatiosV2Enabled,
    isAdPreviewRefreshEnabled,
    hotspots,
    onChangeEditImageId,
  } = props;
  let { adPreviewConfig } = props;

  if (adPreviewConfig === undefined) {
    const refreshPmaxAdPreviewConfig = isAdPreviewRefreshEnabled ? PMaxAdPreviewConfigV3 : PMaxAdPreviewConfigV2;
    adPreviewConfig = isPMaxAspectRatiosV2Enabled ? refreshPmaxAdPreviewConfig : PMaxAdPreviewConfig;
  }

  const extraPlacementInfo = useMemo(() => {
    if (extraPlacementInfoProp) {
      return extraPlacementInfoProp;
    }
    return getPMaxExtraPlacementInfo(logoPreviewEnabled);
  }, [extraPlacementInfoProp, logoPreviewEnabled]);

  const hasSurfaceChoicePivot = !isMSANAd;

  const adDataProps = ['shortHeadline', 'longHeadline', 'description', 'url', 'displayUrl', 'callToAction', 'businessName'];
  const currentAdData = _.pick(props, adDataProps);
  const [adData, setAdData] = React.useState(currentAdData);

  const isAdDataUpdated = useCallback(newAdData => (newAdData.length !== adData.length || !_.isMatch(newAdData, adData)), [adData]);
  const debouncedSetAdData = useMemo(() => _.debounce(setAdData, 500), []);
  if (isAdDataUpdated(currentAdData)) {
    debouncedSetAdData.cancel();
    debouncedSetAdData(currentAdData);
  }

  const getImageIndexFromId = useCallback((id) => {
    const index = _.findIndex(imageList, placement => _.first(placement).id === id && !_.first(placement).isLogo && !_.first(placement).isPortrait);
    return index >= 0 ? index : null;
  }, [imageList]);

  const getPreviewImageIndex = useCallback((idRequestedByImageExperience) => {
    let index = getImageIndexFromId(idRequestedByImageExperience);

    if (!index) {
      const firstImageIndex = _.findIndex(imageList, placement => !_.first(placement).isLogo);
      index = firstImageIndex >= 0 ? firstImageIndex : null;
    }

    return index;
  }, [getImageIndexFromId, imageList]);

  const { previewImageId, previewLogoId } = previewImagesSharedState;

  let previewImageIndex = null;
  if (imageList && imageList.length > 0) {
    previewImageIndex = getPreviewImageIndex(previewImageId);
  }

  const requiredAspectRatios = isMMAV2Enabled ? audienceMMAV2RequiredAspectRatios : pmaxRequiredAspectRatios;
  const canExcludeAspectRatio = useCallback((typeToCheck) => {
    const minCount = requiredAspectRatios[typeToCheck];
    if (!minCount) {
      return true;
    }

    let typeCount = 0;
    _.each(imageList, imageArray =>
      _.each(imageArray, (imageObj) => {
        if (imageObj.isIncluded && typeToCheck === imageObj.type) {
          typeCount += 1;
        }
      }));

    return (typeCount - 1) >= minCount;
  }, [imageList]);

  const onAddAspectRatio = useCallback((type) => {
    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = true;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true, editingPlacementType: '' });
    updateImageList(newImageList);
  }, [imageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState, updateImageList]);

  const onRemoveAspectRatio = useCallback((type) => {
    const newImageList = [...imageList];
    _.find(newImageList[previewImageIndex], imagePlacement => imagePlacement.type === type).isIncluded = false;
    setPreviewImagesSharedState({ ...previewImagesSharedState, isImageListUpdatedByPreview: true, editingPlacementType: '' });
    updateImageList(newImageList);
  }, [imageList, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState, updateImageList]);

  const onEdit = useCallback((type, placementType) => {
    if (activity && activity.signal) {
      activity.signal(`Perf max ad preview: re-crop clicked, isMSANAd:${isMSANAd}, type:${type}`);
    }
    const cropImageObj = _.find(imageList[previewImageIndex], imagePlacement => imagePlacement.type === type);
    setPreviewImagesSharedState({ ...previewImagesSharedState, cropImageObj, editingPlacementType: placementType });

    if (onChangeEditImageId && imageList[previewImageIndex]) {
      onChangeEditImageId(_.first(imageList[previewImageIndex]).id);
    }
  }, [activity, imageList, isMSANAd, onChangeEditImageId, previewImageIndex, previewImagesSharedState, setPreviewImagesSharedState]);

  const truncatedAdData = useMemo(() => {
    const validAdData = { ...adData };

    if (validAdData.shortHeadline && validAdData.shortHeadline.length > shortHeadlineMaxLength) {
      validAdData.shortHeadline = validAdData.shortHeadline.substring(0, shortHeadlineMaxLength - 1);
    }

    if (validAdData.longHeadline && validAdData.longHeadline.length > longHeadlineMaxLength) {
      validAdData.longHeadline = validAdData.longHeadline.substring(0, longHeadlineMaxLength - 1);
    }

    if (validAdData.description && validAdData.description.length > descriptionMaxLength) {
      validAdData.description = validAdData.description.substring(0, descriptionMaxLength - 1);
    }

    return _.defaults(validAdData, {
      shortHeadline: i18n.getString(_TL_('Short headline')),
      longHeadline: i18n.getString(_TL_('Long headline')),
      description: i18n.getString(_TL_('Ad text')),
      businessName: i18n.getString(_TL_('Business name')),
      callToAction: i18n.getString(_TL_('Please select language first')),
      url: 'https://example.com',
    });
  }, [adData, i18n]);

  const classes = useDefaultStyles(getAdPreviewStyles);
  const [surfaceChoice, setSurfaceChoice] = useState({});
  // use extensionCount and extensionRerenderFlag to force rerender when extension count decreases
  const extensionCount = useRef(0);
  const [extensionRerenderFlag, setExtensionRerenderFlag] = useState(0);

  const {
    onUpdateImageExperienceImageList,
    imageExperiencePlacementSets: placementSets,
  } = useImageExperienceAdPreviewModel(adPreviewConfig, previewPlaceholder, isImageFittingEnabled);

  useEffect(() => {
    // get correct image index fitler out logo
    const imageListWithoutLogo = _.compact(_.map(imageList, (placementList) => {
      const placements = _.filter(placementList, placement => !placement.isLogo && !placement.isPortrait);
      return !_.isEmpty(placements) ? placements : null;
    })) || [];

    // get current preview image
    const imageToPreview = _.find(imageListWithoutLogo, (placementList) => {
      const {
        id, idString, baseId, baseImage,
      } = _.first(placementList) || {};
      const { id: baseImageId, idString: baseImageIdString } = baseImage || {};
      return id === previewImageId || idString === previewImageId || baseId === previewImageId || baseImageId === previewImageId || baseImageIdString === previewImageId;
    }) || imageListWithoutLogo[0];

    _.debounce(onUpdateImageExperienceImageList, 100)([imageToPreview], null);
    // !! when *previewImageSharedState* changes update the image list, this component relies on in-place updates to imageList
  }, [previewImagesSharedState, imageList, onUpdateImageExperienceImageList, previewImageId]);

  const curPlacementList = useMemo(() => {
    const { placementList: pl } = _.find(placementSets, ({ placementSetName }) => !hasSurfaceChoicePivot || placementSetName === surfaceChoice) || {};
    if (isAdExtensionPreviewEnabled && surfaceChoice === 'Bing') {
      const bingSearchAdPlacement = _.find(pl, ({ placementMeta }) => placementMeta.placementType === EPT.BingSearch.Text);
      // extend the placement list to add new placements for ad extensions
      if (bingSearchAdPlacement) {
        const placementListWithExtensions = [
          ...pl,
          // add additional property to identity the extension type to render
          ...AdExtensionTypes.reduce((pls, adExtensionType) => {
            if (_.has(extensionsAssociations, adExtensionType) && extensionsAssociations[adExtensionType].length > 0) {
              pls.push({ ...bingSearchAdPlacement, adExtensionType });
            }
            return pls;
          }, []),
        ];
        const preExtensionCount = extensionCount.current;
        extensionCount.current = placementListWithExtensions.length - pl.length;
        if (preExtensionCount > extensionCount.current) {
          setExtensionRerenderFlag(prevState => (prevState + 1) % 1000);
        }
        return placementListWithExtensions;
      }
    }
    return pl;
  }, [extensionsAssociations, hasSurfaceChoicePivot, isAdExtensionPreviewEnabled, placementSets, surfaceChoice]);

  const isSizeValid = useCallback((placement) => {
    const { placementMeta, imageMeta } = placement || {};
    const { minSize } = placementMeta || {};
    const [minWidth, minHeight] = minSize || [];
    const { width, height } = imageMeta || {};
    return width >= minWidth && height >= minHeight;
  }, []);

  const getFirstElement = useCallback((item) => {
    if (_.isArray(item)) {
      return _.first(item);
    }
    return item;
  }, []);

  const curPreviewLogo = useMemo(() => {
    const allLogos = _.filter(imageList, imageArr => getFirstElement(imageArr).isLogo === true);
    let curLogos = previewLogoId && _.find(allLogos, logo => _.first(logo).Id === previewLogoId);
    curLogos = curLogos || _.first(allLogos);

    // currently only square logo is supported, 4x1 logo are ignored
    const curLogoRatio = _.find(curLogos, logo => logo.isIncluded === true && logo.type === 'Image1x1');
    return curLogoRatio;
  }, [getFirstElement, imageList, previewLogoId]);

  const renderAdExtensionPreivew = useCallback((placement, adExtensionType) => {
    const AdExtensionPreviewComponent = AdPreviewAdExtensionComponents[adExtensionType];
    if (AdExtensionPreviewComponent) {
      return <AdExtensionPreviewComponent {...placement} extensionData={getExtensionData(extensionsAssociations, adExtensionType, extensionFormatters)} />;
    }
    return null;
  }, [extensionFormatters, extensionsAssociations]);

  const onRenderPlacement = useCallback((placement, AdPlacementComponent) => {
    const {
      placementMeta, imageMeta, hasImageExperienceData, adExtensionType,
    } = placement || {};
    const isPlaceholderImage = imageMeta && (imageMeta.url === previewPlaceholder);
    const { minSize, placementType } = placementMeta || {};
    const [minWidth, minHeight] = minSize || [];
    const isValid = isSizeValid(placement);
    const {
      hasLogo, customClassName, imagePreviewHeight, previewTitle, disclaimerMessage,
    } = extraPlacementInfo[placementType] || {};

    return (
      <div className={classNames(customPlacementContainerClassName || classes.placementContainerClassName)}>
        {hasImageExperienceData && !isValid &&
          <span className="placement-validation-message action-label">
            {
              i18n.getString(_TL_('To include this aspect ratio, add a new image that is at least {{width}} x {{height}} pixels.'), {
                width: minWidth,
                height: minHeight,
              })
            }
          </span>
        }
        <AdPlacementComponent
          isEnabled={!!placement.isPlacementIncluded && isValid && !isPlaceholderImage}
          placementData={placement}
          {...truncatedAdData}
          finalUrl={truncatedAdData.url}
          i18n={i18n}
          logo={hasLogo && curPreviewLogo}
          hotspots={hotspots}
          customClassName={customClassName || undefined}
          imagePreviewHeight={imagePreviewHeight || undefined}
          adExtensionContainerClassName={classes.adExtensionContainer}
        >
          {adExtensionType && renderAdExtensionPreivew(placement, adExtensionType)}
        </AdPlacementComponent>
        {isMSANAd && previewTitle && (
          <div className="preview-string">{i18n.getString(previewTitle)}</div>
        )}
        {isMSANAd && disclaimerMessage && (
          <div className="additional-disclaimer-message">{i18n.getString(disclaimerMessage)}</div>
        )}
      </div>
    );
  }, [classes.adExtensionContainer, classes.placementContainerClassName, curPreviewLogo, customPlacementContainerClassName, extraPlacementInfo, i18n, isMSANAd, isSizeValid, renderAdExtensionPreivew, truncatedAdData]);

  const onRenderCarouselHeader = useCallback((placement) => {
    const { placementMeta, isPlacementIncluded, hasImageExperienceData } = placement || {};
    const { imageType, placementType } = placementMeta || {};
    if (!placementType) {
      return null;
    }
    const canBeExcluded = canExcludeAspectRatio(imageType);
    const {
      aspectRatioUIString, previewTitle, previewHeaderTitle, hideHeaderButtons = false,
    } = extraPlacementInfo[placementMeta.placementType] || {};
    const isValid = isSizeValid(placement);
    return (
      <div className={classNames(classes.carouselLayoutHeader, customPlacementHeaderClassName)}>
        {isMSANAd ? (
          <div className="ad-preview-title" >
            <span className="preview-title-string-key">{previewHeaderTitle ? i18n.getString(previewHeaderTitle) : i18n.getString(previewTitle)}</span>
          </div>
        ) : (
          <div className="carousel-header-texts">
            <span className="carousel-header-ratio">{aspectRatioUIString}</span>
            <span className="carousel-header-title">{i18n.getString(previewTitle)}</span>
          </div>
        )}
        {hasImageExperienceData && isValid && !hideHeaderButtons && (
          <div className="ad-preview-actions">
            {isPlacementIncluded && <DefaultButton className="crop-image-button" onClick={() => onEdit(imageType, placementType)} text={i18n.getString(_TL_('Edit aspect ratio'))} />}
            {isPlacementIncluded && <DefaultButton className="exclude-aspect-ratio-button" onClick={() => onRemoveAspectRatio(imageType)} text={i18n.getString(_TL_('Exclude'))} disabled={!canBeExcluded} />}
            {!isPlacementIncluded && <DefaultButton className="include-aspect-ratio-button" onClick={() => onAddAspectRatio(imageType)} text={i18n.getString(_TL_('Include'))} />}
          </div>
        )}
      </div>
    );
  }, [canExcludeAspectRatio, classes.carouselLayoutHeader, customPlacementHeaderClassName, extraPlacementInfo, i18n, isMSANAd, isSizeValid, onAddAspectRatio, onEdit, onRemoveAspectRatio]);

  const carouselLayoutClassName = useMemo(() => classNames(
    classes.carouselLayoutClassName,
    surfaceChoice === 'Msn' ? classes.carouselControlsMsn : classes.carouselControlsOthers
  ), [classes, surfaceChoice]);

  return (
    <div className={classes.adPreviewContainer}>
      {hasSurfaceChoicePivot &&
        <PerfMaxAdPreviewControls onSurfaceChange={setSurfaceChoice} onPlacementFilterChange={_.noop} showAdPreviewControlsV2={isAdPreviewRefreshEnabled && isPMaxAspectRatiosV2Enabled} />}
      <AdPreviewCarouselLayoutEx
        placementList={curPlacementList}
        onRenderPlacement={onRenderPlacement}
        onRenderHeader={onRenderCarouselHeader}
        enableAutoPlay={_.isNull(enableAutoPlay) ? isMSANAd : enableAutoPlay}
        i18n={i18n}
        useCustomCarousel={isAdExtensionPreviewEnabled}
        customCarouselRootClassName={isAdExtensionPreviewEnabled ? classes.customCarouselRoot : carouselLayoutClassName}
        resetOn={`${surfaceChoice}-${extensionRerenderFlag}`}
      />
    </div>
  );
};

PerfMaxAdPreview.propTypes = {
  adPreviewConfig: PropTypes.arrayOf(PropTypes.shape({})),
  extraPlacementInfo: PropTypes.shape({}),
  imageList: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  updateImageList: PropTypes.func.isRequired,
  previewImagesSharedState: PropTypes.shape({
    previewImageId: PropTypes.string,
    previewLogoId: PropTypes.string,
  }).isRequired,
  setPreviewImagesSharedState: PropTypes.func.isRequired,
  isMSANAd: PropTypes.bool,
  enableAutoPlay: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  logoPreviewEnabled: PropTypes.bool,
  customPlacementContainerClassName: PropTypes.string,
  customPlacementHeaderClassName: PropTypes.string,
  isAdExtensionPreviewEnabled: PropTypes.bool,
  extensionsAssociations: PropTypes.shape({}),
  extensionFormatters: PropTypes.shape({}),
  isMMAV2Enabled: PropTypes.bool,
  isImageFittingEnabled: PropTypes.bool,
  activity: PropTypes.shape({}),
  isPMaxAspectRatiosV2Enabled: PropTypes.bool,
  isAdPreviewRefreshEnabled: PropTypes.bool,
};

PerfMaxAdPreview.defaultProps = {
  adPreviewConfig: undefined,
  extraPlacementInfo: undefined,
  imageList: null,
  isMSANAd: false,
  enableAutoPlay: null,
  logoPreviewEnabled: false,
  customPlacementContainerClassName: undefined,
  customPlacementHeaderClassName: undefined,
  isAdExtensionPreviewEnabled: false,
  extensionsAssociations: {},
  extensionFormatters: {},
  isMMAV2Enabled: false,
  isImageFittingEnabled: false,
  activity: undefined,
  isPMaxAspectRatiosV2Enabled: false,
  isAdPreviewRefreshEnabled: false,
};
